import axios from 'axios';
import { trackError } from '@/utils/analytics';

// === Global Axios Defaults ===
axios.defaults.timeout = 20000; // 20 seconds max per request

// === Axios Retry Interceptor ===
const MAX_RETRIES = 3;
const MAX_RETRIES_WRITE = 1; // Retry POST/PUT once for timeouts only
const RETRY_DELAY_BASE = 1000;

const retryCountMap = new WeakMap();

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (!config || config._noRetry) return Promise.reject(error);

    const method = (config.method || '').toLowerCase();
    const isRead = ['get', 'head', 'options'].includes(method);
    const status = error?.response?.status;
    const isNetworkError = !status && (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.message?.includes('Network Error'));
    const isServerError = status >= 500 || status === 408 || status === 429;

    // For read requests: retry on server errors and network errors
    // For write requests: retry ONLY on network timeout (not server errors to avoid duplicates)
    const shouldRetry = isRead
      ? (isServerError || isNetworkError)
      : (isNetworkError && method !== 'delete');

    if (!shouldRetry) {
      return Promise.reject(error);
    }

    const maxRetries = isRead ? MAX_RETRIES : MAX_RETRIES_WRITE;
    const retryCount = retryCountMap.get(config) || 0;

    if (retryCount >= maxRetries) {
      const url = (config.url || 'unknown').replace(/\?.*/, '');
      trackError(
        'api_error',
        `${method.toUpperCase()} ${url} failed: ${status || 'network_error'} after ${maxRetries} retries`,
        error?.response?.data?.detail || error?.message || ''
      );
      return Promise.reject(error);
    }

    retryCountMap.set(config, retryCount + 1);
    const delay = RETRY_DELAY_BASE * Math.pow(2, retryCount);
    await new Promise(resolve => setTimeout(resolve, delay));
    return axios(config);
  }
);

// === Request Interceptor: Add timestamp to prevent stale cache ===
axios.interceptors.request.use((config) => {
  // Skip cache-busting for external URLs
  if (config.url && !config.url.includes('localhost') && !config.url.includes(process.env.REACT_APP_BACKEND_URL || '')) {
    return config;
  }
  return config;
});

// === Local Storage Cache ===
const CACHE_PREFIX = 'dm_cache_';
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

export const cachedGet = async (url, options = {}) => {
  const cacheKey = CACHE_PREFIX + btoa(url).slice(0, 50);
  
  try {
    const response = await axios.get(url, options);
    if (response.data) {
      try {
        localStorage.setItem(cacheKey, JSON.stringify({
          data: response.data,
          timestamp: Date.now()
        }));
      } catch (e) {
        clearOldCaches();
      }
    }
    return response;
  } catch (error) {
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey));
      if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
        return { data: cached.data, _fromCache: true };
      }
    } catch (e) {
      console.warn('apiResilience: cache fallback unavailable', e);
    }
    throw error;
  }
};

function clearOldCaches() {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(CACHE_PREFIX));
    keys.slice(0, Math.ceil(keys.length / 2)).forEach(k => localStorage.removeItem(k));
  } catch (e) {
    console.warn('apiResilience: cache cleanup failed', e);
  }
}

// === Online/Offline Detection ===
let isOnline = navigator.onLine;
const listeners = new Set();

export const onConnectionChange = (callback) => {
  listeners.add(callback);
  return () => listeners.delete(callback);
};

export const getConnectionStatus = () => isOnline;

window.addEventListener('online', () => {
  isOnline = true;
  listeners.forEach(cb => cb(true));
});

window.addEventListener('offline', () => {
  isOnline = false;
  listeners.forEach(cb => cb(false));
});

export default axios;
